import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { SUPPORT_EMAIL } from '../lib/config';
import { api } from '../api/client';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

/**
 * 套餐升级页 — 套餐对比 + 真实支付流程
 *
 * 支付渠道：
 * - Waffo：跳转到 Checkout 页面（自动续费订阅）
 * - 虎皮椒微信 / 支付宝：扫码 + 轮询（一次性购买 30 天，到期顺延）
 * - 老支付宝通道：扫码 + 轮询
 * 支付成功后自动刷新用户信息并显示成功状态
 */

type PayChannel = 'alipay' | 'wxpay' | 'xunhupay_alipay' | 'waffo';

interface QrOrder {
  orderId: string;
  qrUrl: string;
  /** 轮询用：老支付宝通道 or 虎皮椒 */
  kind: 'alipay' | 'xunhupay';
  channel: 'wxpay' | 'alipay';
}

export default function Upgrade() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<PayChannel>('wxpay');

  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';

  // 获取套餐信息和可用渠道
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.getPlans(),
  });

  // 创建 Waffo Checkout
  const waffoMutation = useMutation({
    mutationFn: () =>
      api.createWaffoCheckout(
        'pro',
        `${window.location.origin}/settings?payment=success`,
        `${window.location.origin}/upgrade?payment=canceled`,
      ),
    onSuccess: (data) => {
      window.location.href = data.checkoutUrl;
    },
  });

  // 扫码支付（虎皮椒微信/支付宝、老支付宝通道）统一状态机
  const [qrOrder, setQrOrder] = useState<QrOrder | null>(null);
  const [qrStatus, setQrStatus] = useState<'idle' | 'creating' | 'waiting' | 'success' | 'failed'>('idle');
  const [qrError, setQrError] = useState('');

  const alipayMutation = useMutation({
    mutationFn: () =>
      api.createAlipayOrder('pro', `${window.location.origin}/settings?payment=success`),
    onMutate: () => setQrStatus('creating'),
    onSuccess: (data) => {
      setQrOrder({
        orderId: data.orderId,
        qrUrl: data.qrUrl,
        kind: 'alipay',
        channel: 'alipay',
      });
      setQrStatus('waiting');
    },
    onError: (err) => {
      setQrStatus('failed');
      setQrError((err as Error).message);
    },
  });

  const xunhupayMutation = useMutation({
    mutationFn: (payChannel: 'wxpay' | 'alipay') =>
      api.createXunhupayOrder('pro', payChannel, `${window.location.origin}/settings?payment=success`),
    onMutate: () => setQrStatus('creating'),
    onSuccess: (data, payChannel) => {
      setQrOrder({
        orderId: data.orderId,
        qrUrl: data.qrUrl,
        kind: 'xunhupay',
        channel: payChannel,
      });
      setQrStatus('waiting');
    },
    onError: (err) => {
      setQrStatus('failed');
      setQrError((err as Error).message);
    },
  });

  // 扫码订单轮询
  useEffect(() => {
    if (qrStatus !== 'waiting' || !qrOrder) return;

    const interval = setInterval(async () => {
      try {
        const status = qrOrder.kind === 'xunhupay'
          ? await api.checkXunhupayStatus(qrOrder.orderId, qrOrder.channel)
          : await api.checkAlipayStatus(qrOrder.orderId);
        if (status === 'succeeded') {
          setQrStatus('success');
          await refreshUser();
          queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
          clearInterval(interval);
        } else if (status === 'failed') {
          setQrStatus('failed');
          setQrError('Payment failed');
          clearInterval(interval);
        }
      } catch {
        // 轮询失败，继续尝试
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [qrStatus, qrOrder, refreshUser, queryClient]);

  // 切换渠道时重置订单状态
  useEffect(() => {
    setQrOrder(null);
    setQrStatus('idle');
    setQrError('');
  }, [channel]);

  // 可用渠道
  const channels = plansData?.channels ?? [];
  const hasAlipay = channels.includes('alipay');
  const hasWxpay = channels.includes('xunhupay_wxpay');
  const hasXunhupayAlipay = channels.includes('xunhupay_alipay');
  // 虎皮椒支付宝通道可用时隐藏老支付宝通道（同一支付方式，新通道优先）
  const showLegacyAlipay = hasAlipay && !hasXunhupayAlipay;
  const hasWaffo = channels.includes('waffo');

  // 默认渠道优先级：微信 > 虎皮椒支付宝 > 老支付宝 > Waffo
  useEffect(() => {
    if (channels.length === 0) return;
    if (channels.includes('xunhupay_wxpay')) setChannel('wxpay');
    else if (channels.includes('xunhupay_alipay')) setChannel('xunhupay_alipay');
    else if (channels.includes('alipay')) setChannel('alipay');
    else if (channels.includes('waffo')) setChannel('waffo');
  }, [channels]);

  const isCnyChannel = channel === 'alipay' || channel === 'wxpay' || channel === 'xunhupay_alipay';

  const compareRows: { label: string; free: string; pro: string }[] = [
    { label: t('upgrade.row.apps'), free: t('upgrade.row.apps.free'), pro: t('upgrade.row.apps.pro') },
    { label: t('upgrade.row.apk'), free: '✓', pro: '✓' },
    { label: t('upgrade.row.sync'), free: '✓', pro: '✓' },
    { label: t('upgrade.row.qr'), free: '✓', pro: '✓' },
    { label: t('upgrade.row.watermark'), free: t('upgrade.row.watermark.free'), pro: t('upgrade.row.watermark.pro') },
    { label: t('upgrade.row.storage'), free: '100MB', pro: '1GB' },
    { label: t('upgrade.row.builds'), free: t('upgrade.row.builds.free'), pro: t('upgrade.row.builds.pro') },
    { label: t('upgrade.row.multi'), free: '—', pro: '✓' },
  ];

  const notes: TranslationKey[] = [
    'upgrade.notes.1',
    'upgrade.notes.2',
    'upgrade.notes.3',
    'upgrade.notes.4',
  ];

  if (isPro) {
    return (
      <div className="page-wrap">
        <h1 className="page-title-lg">{t('upgrade.pro.title')}</h1>
        <p className="page-sub">{t('upgrade.pro.sub')}</p>
        <div className="card">
          <div className="state-box">
            {t('upgrade.pro.current')}
            <span className="plan-badge" style={{ marginLeft: '0.5rem' }}>{t('plan.pro')}</span>
            <span style={{ marginLeft: '0.5rem' }}>{t('upgrade.pro.enjoy')}</span>
          </div>
          <div className="form-actions">
            <Link to="/dashboard" className="btn btn-primary">{t('upgrade.pro.back')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <Link to="/settings" className="back-link">{t('upgrade.back')}</Link>

      <h1 className="page-title-lg">{t('upgrade.title')}</h1>
      <p className="page-sub">{t('upgrade.sub')}</p>

      {/* 套餐对比 */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">{t('upgrade.compare.title')}</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--rule)' }}>
              <th style={{ textAlign: 'left', padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'var(--muted)' }}>
                {t('upgrade.compare.feature')}
              </th>
              <th style={{ textAlign: 'center', padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'var(--muted)' }}>
                {t('upgrade.compare.free')}
              </th>
              <th style={{ textAlign: 'center', padding: '0.6rem 0.75rem', fontSize: '0.78rem', color: 'var(--accent2)' }}>
                {t('upgrade.compare.pro')}
              </th>
            </tr>
          </thead>
          <tbody>
            {compareRows.map((row) => (
              <tr key={row.label} style={{ borderBottom: '1px solid var(--rule)' }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{row.label}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: 'var(--muted)' }}>
                  {row.free}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: 'var(--accent2)', fontWeight: 600 }}>
                  {row.pro}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700 }}>{t('upgrade.row.price')}</td>
              <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', color: 'var(--muted)' }}>
                {t('upgrade.price.free')}
              </td>
              <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 700, color: 'var(--accent2)' }}>
                {isCnyChannel
                  ? t('upgrade.price.proAlt')
                  : t('upgrade.price.pro')}
                <br />
                <span style={{ fontSize: '0.78rem', fontWeight: 400, color: 'var(--muted)' }}>
                  {isCnyChannel
                    ? t('upgrade.price.pro')
                    : t('upgrade.price.proAlt')}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 支付方式选择 */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">{t('upgrade.pay.title')}</span>
        </div>

        {plansLoading && <div className="state-box">{t('common.loading')}</div>}

        {!plansLoading && channels.length === 0 && (
          <div className="state-box state-error">
            {t('billing.noPaymentMethods')}
          </div>
        )}

        {!plansLoading && channels.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              {hasWxpay && (
                <button
                  className={`btn ${channel === 'wxpay' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: '1 1 140px' }}
                  onClick={() => setChannel('wxpay')}
                >
                  {t('upgrade.pay.wxpay')}
                </button>
              )}
              {hasXunhupayAlipay && (
                <button
                  className={`btn ${channel === 'xunhupay_alipay' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: '1 1 140px' }}
                  onClick={() => setChannel('xunhupay_alipay')}
                >
                  {t('upgrade.pay.alipay')}
                </button>
              )}
              {showLegacyAlipay && (
                <button
                  className={`btn ${channel === 'alipay' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: '1 1 140px' }}
                  onClick={() => setChannel('alipay')}
                >
                  {t('upgrade.pay.alipay')}
                </button>
              )}
              {hasWaffo && (
                <button
                  className={`btn ${channel === 'waffo' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: '1 1 140px' }}
                  onClick={() => setChannel('waffo')}
                >
                  {t('upgrade.pay.waffo')}
                </button>
              )}
            </div>

            {/* Waffo：跳转 Checkout（全球卡支付，MoR 模式） */}
            {channel === 'waffo' && (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <button
                  className="btn btn-accent btn-lg"
                  onClick={() => waffoMutation.mutate()}
                  disabled={waffoMutation.isPending}
                  style={{ minWidth: '200px' }}
                >
                  {waffoMutation.isPending ? t('common.loading') : t('landing.pricing.upgradePersonal')}
                </button>
                {waffoMutation.isError && (
                  <div className="field-error" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                    {waffoMutation.error.message}
                  </div>
                )}
                <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.75rem' }}>
                  {t('upgrade.pay.waffoNote')}
                </p>
              </div>
            )}

            {/* 老支付宝通道：扫码 */}
            {channel === 'alipay' && (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                {qrStatus === 'idle' && (
                  <>
                    <button
                      className="btn btn-accent btn-lg"
                      onClick={() => alipayMutation.mutate()}
                      disabled={alipayMutation.isPending}
                      style={{ minWidth: '200px' }}
                    >
                      {alipayMutation.isPending ? t('common.loading') : t('landing.pricing.upgradePersonal')}
                    </button>
                    <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.75rem' }}>
                      {t('upgrade.pay.alipayHint')}
                    </p>
                  </>
                )}

                {(qrStatus === 'creating' || (qrStatus === 'waiting' && !qrOrder?.qrUrl)) && (
                  <div className="state-box">{t('common.loading')}</div>
                )}

                {qrStatus === 'waiting' && qrOrder?.qrUrl && (
                  <>
                    <div style={{
                      width: '200px', height: '200px', margin: '0 auto 1rem',
                      border: '1px solid var(--rule)', borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={qrOrder.qrUrl}
                        alt="Alipay QR code"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--ink2)', marginBottom: '0.5rem' }}>
                      {t('upgrade.pay.alipayHint')}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                      {t('upgrade.pay.autoNote')}
                    </p>
                    <div className="status-dot dot-building" style={{ marginRight: '0.5rem' }} />
                    <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                      {t('billing.waitingPayment')}
                    </span>
                  </>
                )}

                {qrStatus === 'waiting' && !qrOrder?.qrUrl && (
                  <div style={{
                    width: '200px', height: '200px', margin: '0 auto 1rem',
                    background: 'var(--bg2)', border: '1px dashed var(--rule)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', color: 'var(--muted)',
                  }}>
                    {t('upgrade.pay.qrPlaceholder1')}<br />{t('upgrade.pay.qrPlaceholder2')}
                  </div>
                )}

                {qrStatus === 'success' && (
                  <div>
                    <div className="state-box" style={{ borderColor: 'var(--accent3)', color: 'var(--accent3)' }}>
                      ✓ {t('billing.paymentSuccess')}
                    </div>
                    <div className="form-actions" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                      <Link to="/settings" className="btn btn-primary">{t('upgrade.pro.back')}</Link>
                    </div>
                  </div>
                )}

                {(qrStatus === 'failed' || qrError) && (
                  <div className="field-error" style={{ marginBottom: '1rem' }}>
                    {qrError || t('detail.downloadFailed')}
                  </div>
                )}
              </div>
            )}

            {/* 虎皮椒（微信/支付宝）：扫码 + 轮询，一次性购买 30 天 */}
            {(channel === 'wxpay' || channel === 'xunhupay_alipay') && (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                {qrStatus === 'idle' && (
                  <>
                    <button
                      className="btn btn-accent btn-lg"
                      onClick={() => xunhupayMutation.mutate(channel === 'wxpay' ? 'wxpay' : 'alipay')}
                      disabled={xunhupayMutation.isPending}
                      style={{ minWidth: '200px' }}
                    >
                      {xunhupayMutation.isPending ? t('common.loading') : t('landing.pricing.upgradePersonal')}
                    </button>
                    <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.75rem' }}>
                      {channel === 'wxpay' ? t('upgrade.pay.wxpayHint') : t('upgrade.pay.xunhupayAlipayHint')}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                      {t('upgrade.pay.xunhupayNote')}
                    </p>
                  </>
                )}

                {(qrStatus === 'creating' || (qrStatus === 'waiting' && !qrOrder?.qrUrl)) && (
                  <div className="state-box">{t('common.loading')}</div>
                )}

                {qrStatus === 'waiting' && qrOrder?.qrUrl && (
                  <>
                    <div style={{
                      width: '200px', height: '200px', margin: '0 auto 1rem',
                      border: '1px solid var(--rule)', borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={qrOrder.qrUrl}
                        alt="Payment QR code"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--ink2)', marginBottom: '0.5rem' }}>
                      {qrOrder.channel === 'wxpay' ? t('upgrade.pay.wxpayHint') : t('upgrade.pay.xunhupayAlipayHint')}
                    </p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                      {t('upgrade.pay.autoNote')}
                    </p>
                    <div className="status-dot dot-building" style={{ marginRight: '0.5rem' }} />
                    <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                      {t('billing.waitingPayment')}
                    </span>
                  </>
                )}

                {qrStatus === 'waiting' && !qrOrder?.qrUrl && (
                  <div style={{
                    width: '200px', height: '200px', margin: '0 auto 1rem',
                    background: 'var(--bg2)', border: '1px dashed var(--rule)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', color: 'var(--muted)',
                  }}>
                    {t('upgrade.pay.qrPlaceholder1')}<br />{t('upgrade.pay.qrPlaceholder2')}
                  </div>
                )}

                {qrStatus === 'success' && (
                  <div>
                    <div className="state-box" style={{ borderColor: 'var(--accent3)', color: 'var(--accent3)' }}>
                      ✓ {t('billing.paymentSuccess')}
                    </div>
                    <div className="form-actions" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                      <Link to="/settings" className="btn btn-primary">{t('upgrade.pro.back')}</Link>
                    </div>
                  </div>
                )}

                {(qrStatus === 'failed' || qrError) && (
                  <div className="field-error" style={{ marginBottom: '1rem' }}>
                    {qrError || t('detail.downloadFailed')}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="form-actions">
          <button
            className="btn btn-ghost"
            onClick={() => navigate('/settings')}
          >
            {t('upgrade.pay.later')}
          </button>
        </div>
      </div>

      {/* 说明 */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">{t('upgrade.notes.title')}</span>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, fontSize: '0.82rem', color: 'var(--ink2)', lineHeight: 1.8 }}>
          {notes.map((key) => (
            <li key={key}>{t(key, { email: SUPPORT_EMAIL })}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
